import isEqual from "lodash.isequal";

import  { type Order } from "@fitness-app/data-models/entities/Order";

export const INVOICE_ERROR_FILTER = "error";
export type InvoiceFilterValue = boolean | typeof INVOICE_ERROR_FILTER;

export type TableChangeAction =
  | { type: "status"; statuses: Order["status"][] }
  | { type: "products"; products: string[] }
  | { type: "invoice"; invoice: InvoiceFilterValue | null }
  | { type: "pagination"; page: number }
  | { type: "none" };

export interface TableChangeParams {
  pagination: { current?: number };
  filters: {
    status?: (Order["status"] | null)[] | null;
    productInfo?: (string | null)[] | null;
    connectedInvoice?: (InvoiceFilterValue | null)[] | null;
  };
  currentState: {
    currentPage: number;
    filteredStatuses: Order["status"][];
    filteredProducts: string[];
    filteredInvoice: InvoiceFilterValue | null;
  };
}

/**
 * Determines what action should be taken when the Ant Design Table onChange is triggered.
 * This function handles the priority of changes:
 * 1. Status filter change -> reset to page 1
 * 2. Product filter change -> reset to page 1
 * 3. Invoice filter change -> reset to page 1
 * 4. Pagination change -> update page
 *
 * Note: Ant Design Table passes empty arrays [] for filters with no selection,
 * not undefined. This function normalizes these values for proper comparison.
 */
export function getTableChangeAction(params: TableChangeParams): TableChangeAction {
  const { pagination, filters, currentState } = params;

  // Normalize invoice filter: empty array or undefined -> null
  const invoiceFilter = (filters.connectedInvoice?.[0] ?? null);
  const invoiceFilterChanged = invoiceFilter !== currentState.filteredInvoice;

  // Check status filter change
  if (!isEqual(currentState.filteredStatuses, filters.status)) {
    return {
      type: "status",
      statuses: (filters.status ?? []),
    };
  }

  // Check product filter change
  if (!isEqual(currentState.filteredProducts, filters.productInfo)) {
    return {
      type: "products",
      products: (filters.productInfo ?? []) as string[],
    };
  }

  // Check invoice filter change (with proper normalization)
  if (invoiceFilterChanged) {
    return {
      type: "invoice",
      invoice: invoiceFilter,
    };
  }

  // Check pagination change
  if (pagination.current && pagination.current !== currentState.currentPage) {
    return {
      type: "pagination",
      page: pagination.current,
    };
  }

  return { type: "none" };
}
